import { useEffect, useMemo, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { Download, Play, RefreshCw, Trash2 } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  QueryErrorState,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
  toast,
} from '@/components/ui'
import { usePermission } from '@/shell/module-system/permissions'
import { odooUrl } from '@/lib/utils/odoo-url'
import { exportUrl, useClearCache, useReview, useRunNow } from '../api/queries'
import type { CriticalRow, OverduePoRow, ProductRow, QuotedRow, ReviewResult } from '../api/types'
import { DecisionSelect } from '../components/DecisionSelect'
import { ReviewSettingsPanel } from '../components/ReviewSettingsPanel'
import { SuppressionsPanel } from '../components/SuppressionsPanel'

type Tab = 'review' | 'suppressions' | 'settings'
type View = 'act' | 'project' | 'overdue' | 'watch' | 'blind' | 'quoted' | 'caveats'

const VIEW_LABEL: Record<View, string> = {
  act: '1 Act now (critical)',
  project: '2 Active project risk',
  overdue: '3 Overdue POs',
  watch: '4 Watch and hold',
  blind: '5 Blind spots',
  quoted: '6 Quoted, not confirmed',
  caveats: '8 Caveats',
}

const num = (v: number | null | undefined) => (v == null ? '' : v.toLocaleString(undefined, { maximumFractionDigits: 1 }))
const text = (v: string | null | undefined) => v ?? ''

function matches(needle: string, ...fields: (string | null | undefined)[]) {
  if (!needle) return true
  return fields.some((f) => (f ?? '').toLowerCase().includes(needle))
}

export function StockOutReviewPage() {
  const params = useParams({ strict: false }) as { runId?: string }
  const isAdmin = usePermission('admin')
  const review = useReview(params.runId)
  const runNow = useRunNow()
  const clearCache = useClearCache()
  const [tab, setTab] = useState<Tab>('review')
  const [view, setView] = useState<View>('act')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim().toLowerCase()), 200)
    return () => clearTimeout(id)
  }, [search])

  const run = review.data
  const result: ReviewResult | undefined = run?.result ?? undefined
  const summary = result?.summary
  const runId = run?.run_id

  const products = useMemo(
    () => (result?.products ?? []).filter((r) => matches(debounced, r.product, r.default_code, r.top_customer)),
    [result, debounced],
  )
  const byProduct = useMemo(() => new Map((result?.products ?? []).map((p) => [p.product_id, p])), [result])

  const decisionCol = useMemo<ColumnDef<{ product_id: number }, unknown>>(
    () => ({
      id: 'decision',
      header: 'Decision',
      cell: (ctx) => (
        <DecisionSelect
          productId={ctx.row.original.product_id}
          runId={runId}
          prior={byProduct.get(ctx.row.original.product_id)?.prior_decision}
        />
      ),
    }),
    [runId, byProduct],
  )

  const quotesCol = useMemo<ColumnDef<{ quoted_not_confirmed?: number; quotes_exceed_stock?: boolean }, unknown>>(
    () => ({
      id: 'quotes_exceed_stock',
      header: 'Quoted',
      cell: (ctx) => {
        const row = ctx.row.original
        if (!row.quoted_not_confirmed) return ''
        return (
          <span className={row.quotes_exceed_stock ? 'text-warning-text' : undefined}>
            {num(row.quoted_not_confirmed)}
            {row.quotes_exceed_stock ? ' (exceeds stock)' : ''}
          </span>
        )
      },
    }),
    [],
  )

  const criticalCols = useMemo<ColumnDef<CriticalRow, unknown>[]>(
    () => [
      { id: 'product', header: 'Product', cell: (c) => text(c.row.original.product) },
      { id: 'customer', header: 'Customer', cell: (c) => text(c.row.original.customer) },
      {
        id: 'order',
        header: 'Order',
        cell: (c) =>
          c.row.original.order_id ? (
            <a
              href={odooUrl('sale.order', c.row.original.order_id)}
              target="_blank"
              rel="noreferrer"
              className="text-link hover:underline"
            >
              {c.row.original.order}
            </a>
          ) : (
            text(c.row.original.order)
          ),
      },
      { id: 'scheduled_date', header: 'Scheduled', cell: (c) => c.row.original.scheduled_date },
      { id: 'need', header: 'Need', cell: (c) => `${num(c.row.original.need)} ${c.row.original.uom}` },
      { id: 'available', header: 'Available', cell: (c) => num(c.row.original.available) },
      { id: 'incoming', header: 'Incoming PO with ETA', cell: (c) => text(c.row.original.incoming) },
      { id: 'issue', header: 'Issue', cell: (c) => <span className="text-danger-text">{c.row.original.issue}</span> },
      decisionCol as ColumnDef<CriticalRow, unknown>,
    ],
    [decisionCol],
  )

  const projectCols = useMemo<ColumnDef<ProductRow, unknown>[]>(
    () => [
      { id: 'tier', header: 'Tier', cell: (c) => c.row.original.tier.replaceAll('_', ' ') },
      { id: 'product', header: 'Product', cell: (c) => text(c.row.original.product) },
      { id: 'on_hand', header: 'On hand', cell: (c) => `${num(c.row.original.on_hand)} ${c.row.original.uom}` },
      { id: 'committed_out', header: 'Committed out', cell: (c) => num(c.row.original.committed_out) },
      { id: 'incoming', header: 'Incoming with ETA', cell: (c) => text(c.row.original.incoming_text) },
      { id: 'overdue', header: 'Overdue incoming', cell: (c) => text(c.row.original.overdue_text) },
      { id: 'position_b', header: 'Forecast after all', cell: (c) => num(c.row.original.position_b) },
      {
        id: 'top_customer',
        header: 'Top customer',
        cell: (c) =>
          `${text(c.row.original.top_customer)}${c.row.original.concentration ? ` (${Math.round(c.row.original.concentration * 100)}%)` : ''}`,
      },
      { id: 'classification', header: 'Pattern', cell: (c) => c.row.original.classification.replaceAll('_', ' ') },
      {
        id: 'lead',
        header: 'Lead time',
        cell: (c) => `${c.row.original.lead_time_days}d ${c.row.original.lead_time_source.toLowerCase()}`,
      },
      {
        id: 'buy',
        header: 'Suggested buy',
        cell: (c) =>
          c.row.original.buy_note ??
          `${num(c.row.original.suggested_buy_low)} to ${num(c.row.original.suggested_buy_high)}`,
      },
      { id: 'question', header: 'Question for customer', cell: (c) => text(c.row.original.question_for_customer) },
      quotesCol as ColumnDef<ProductRow, unknown>,
      decisionCol as ColumnDef<ProductRow, unknown>,
    ],
    [decisionCol, quotesCol],
  )

  const watchCols = useMemo<ColumnDef<ProductRow, unknown>[]>(
    () => [
      { id: 'tier', header: 'Tier', cell: (c) => c.row.original.tier },
      { id: 'product', header: 'Product', cell: (c) => text(c.row.original.product) },
      { id: 'available', header: 'Available', cell: (c) => `${num(c.row.original.available)} ${c.row.original.uom}` },
      { id: 'committed_out', header: 'Committed out', cell: (c) => num(c.row.original.committed_out) },
      { id: 'classification', header: 'Pattern', cell: (c) => c.row.original.classification.replaceAll('_', ' ') },
      { id: 'cover', header: 'Cover days', cell: (c) => num(c.row.original.cover_days) },
      quotesCol as ColumnDef<ProductRow, unknown>,
      { id: 'notes', header: 'Notes', cell: (c) => c.row.original.notes },
      decisionCol as ColumnDef<ProductRow, unknown>,
    ],
    [decisionCol, quotesCol],
  )

  const blindCols = useMemo<ColumnDef<ProductRow, unknown>[]>(
    () => [
      { id: 'product', header: 'Product', cell: (c) => text(c.row.original.product) },
      { id: 'tier', header: 'Tier', cell: (c) => c.row.original.tier },
      { id: 'issue', header: 'Issue', cell: (c) => c.row.original.blind_spot_reasons.join('; ') },
      {
        id: 'lead',
        header: 'Lead time',
        cell: (c) => `${c.row.original.lead_time_days}d ${c.row.original.lead_time_source.toLowerCase()}`,
      },
    ],
    [],
  )

  const overdueCols = useMemo<ColumnDef<OverduePoRow, unknown>[]>(
    () => [
      {
        id: 'po',
        header: 'PO',
        cell: (c) =>
          c.row.original.po_id ? (
            <a
              href={odooUrl('purchase.order', c.row.original.po_id)}
              target="_blank"
              rel="noreferrer"
              className="text-link hover:underline"
            >
              {c.row.original.po}
            </a>
          ) : (
            c.row.original.po
          ),
      },
      { id: 'vendor', header: 'Vendor', cell: (c) => text(c.row.original.vendor) },
      { id: 'product', header: 'Product', cell: (c) => text(c.row.original.product) },
      { id: 'remaining', header: 'Remaining', cell: (c) => `${num(c.row.original.remaining)} ${c.row.original.uom}` },
      { id: 'eta', header: 'ETA', cell: (c) => c.row.original.eta },
      { id: 'days', header: 'Days overdue', cell: (c) => c.row.original.days_overdue },
      { id: 'blocks', header: 'What it blocks', cell: (c) => c.row.original.blocks },
    ],
    [],
  )

  const quotedCols = useMemo<ColumnDef<QuotedRow, unknown>[]>(
    () => [
      { id: 'product', header: 'Product', cell: (c) => text(c.row.original.product) },
      { id: 'qty', header: 'Quoted', cell: (c) => `${num(c.row.original.quoted_qty)} ${c.row.original.uom}` },
      { id: 'available', header: 'Available', cell: (c) => num(c.row.original.available) },
      {
        id: 'exceeds',
        header: 'Exceeds stock',
        cell: (c) =>
          c.row.original.quotes_exceed_stock ? <span className="text-warning-text">Yes</span> : '',
      },
      { id: 'quotes', header: 'Quotes', cell: (c) => c.row.original.quotes.join(', ') },
      { id: 'customers', header: 'Customers', cell: (c) => c.row.original.customers.join(', ') },
    ],
    [],
  )

  const table = (() => {
    if (!result) return null
    const common = { loading: review.isLoading, maxHeight: '100%', moduleId: 'stock-out-review' }
    const empty = <EmptyState variant="no-match" title="Nothing here" description="No rows for this view." />
    switch (view) {
      case 'act':
        return (
          <DataTable
            {...common}
            data={result.critical.filter((r) => matches(debounced, r.product, r.customer, r.order))}
            columns={criticalCols}
            getRowId={(r, i) => `${r.product_id}-${r.order}-${i}`}
            emptyState={empty}
          />
        )
      case 'project':
        return (
          <DataTable
            {...common}
            data={products.filter((r) => ['OVERDUE_PO_DEPENDENT', 'HIGH', 'WATCH'].includes(r.tier))}
            columns={projectCols}
            getRowId={(r) => String(r.product_id)}
            emptyState={empty}
          />
        )
      case 'overdue':
        return (
          <DataTable
            {...common}
            data={result.overdue_pos
              .filter((r) => matches(debounced, r.product, r.po, r.vendor))
              .sort((a, b) => a.days_overdue - b.days_overdue)}
            columns={overdueCols}
            getRowId={(r, i) => `${r.po}-${r.product_id}-${i}`}
            emptyState={empty}
          />
        )
      case 'watch':
        return (
          <DataTable
            {...common}
            data={products.filter((r) => r.tier === 'WATCH' || r.tier === 'HOLD')}
            columns={watchCols}
            getRowId={(r) => String(r.product_id)}
            emptyState={empty}
          />
        )
      case 'blind':
        return (
          <DataTable
            {...common}
            data={products.filter((r) => r.blind_spot_reasons.length > 0)}
            columns={blindCols}
            getRowId={(r) => String(r.product_id)}
            emptyState={empty}
          />
        )
      case 'quoted':
        return (
          <DataTable
            {...common}
            data={result.quoted.filter((r) => matches(debounced, r.product, r.default_code))}
            columns={quotedCols}
            getRowId={(r) => String(r.product_id)}
            emptyState={empty}
          />
        )
      case 'caveats':
        return <Caveats result={result} />
    }
  })()

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Stock-Out Review"
        description={
          result
            ? `Weekly review for ${result.today}. Committed demand is confirmed orders only; quotes and RFQs are listed separately.`
            : 'Weekly stock-out risk review for the purchasing meeting.'
        }
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard
            variant="inline"
            label="Critical"
            value={(summary?.CRITICAL ?? 0).toLocaleString()}
            valueTone={(summary?.CRITICAL ?? 0) > 0 ? 'danger' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Chase PO"
            value={(summary?.OVERDUE_PO_DEPENDENT ?? 0).toLocaleString()}
            valueTone={(summary?.OVERDUE_PO_DEPENDENT ?? 0) > 0 ? 'warning' : 'default'}
          />
          <MetricCard variant="inline" label="High" value={(summary?.HIGH ?? 0).toLocaleString()} />
          <MetricCard variant="inline" label="Watch" value={(summary?.WATCH ?? 0).toLocaleString()} />
          <MetricCard variant="inline" label="Hold" value={(summary?.HOLD ?? 0).toLocaleString()} />
          <MetricCard variant="inline" label="Blind spots" value={(summary?.BLIND_SPOT ?? 0).toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <ToolbarLeft>
            <ViewToggle
              aria-label="Section"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'review', label: 'Review' },
                { value: 'suppressions', label: 'Suppressions' },
                ...(isAdmin ? [{ value: 'settings' as Tab, label: 'Settings' }] : []),
              ]}
            />
          </ToolbarLeft>
          <ToolbarRight>
            {runId && run?.status === 'complete' ? (
              <Button variant="secondary" size="sm" onClick={() => window.open(exportUrl(runId), '_blank')}>
                <Download size={14} /> Excel
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              loading={runNow.isPending}
              onClick={() =>
                runNow.mutate(
                  { send_dm: false },
                  {
                    onSuccess: () => toast.success('Run queued. Refresh in a minute to see it.'),
                    onError: (e) => toast.danger(`Run failed to queue${e instanceof Error ? `: ${e.message}` : ''}`),
                  },
                )
              }
            >
              <Play size={14} /> Run now
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Clear cache"
              title="Clear cache"
              loading={clearCache.isPending}
              onClick={() => clearCache.mutate(undefined, { onSuccess: () => toast.success('Cache cleared') })}
            >
              <Trash2 size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={review.isFetching && !review.isLoading}
              onClick={() => void review.refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        {tab === 'review' ? (
          <>
            <Toolbar>
              <SearchInput
                containerClassName="flex-1 max-w-none"
                placeholder="Search product, customer, order, PO…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
              />
            </Toolbar>
            <Toolbar>
              <ToolbarLeft>
                <Select aria-label="View" value={view} onChange={(e) => setView(e.target.value as View)}>
                  {(Object.keys(VIEW_LABEL) as View[]).map((v) => (
                    <option key={v} value={v}>
                      {VIEW_LABEL[v]}
                    </option>
                  ))}
                </Select>
              </ToolbarLeft>
              <ToolbarRight>
                <ToolbarCount>
                  {run?.finished_at ? `Run ${new Date(run.finished_at).toLocaleString()}` : ''}
                </ToolbarCount>
              </ToolbarRight>
            </Toolbar>
            <div className="flex-1 min-h-0 overflow-auto bg-canvas p-3">
              {review.isError ? (
                <QueryErrorState error={review.error} onRetry={() => void review.refetch()} subject="stock-out review" />
              ) : run?.status === 'failed' ? (
                <EmptyState variant="no-data" title="This run failed" description={run.error ?? ''} />
              ) : review.isLoading ? (
                <div className="py-6 text-center text-sm text-tertiary">Loading…</div>
              ) : (
                table
              )}
            </div>
          </>
        ) : tab === 'suppressions' ? (
          <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
            <SuppressionsPanel />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
            <ReviewSettingsPanel />
          </div>
        )}
      </div>
    </div>
  )
}

function Caveats({ result }: { result: ReviewResult }) {
  const c = result.caveats
  const s = result.summary
  const rows: [string, string][] = [
    ['Products scanned', String(s.products_scanned)],
    ['Products excluded', String(s.products_excluded)],
    ...Object.entries(c.excluded).map(([k, v]) => [`Excluded: ${k}`, String(v)] as [string, string]),
    ...c.notes.map((n) => ['Note', n] as [string, string]),
    ...c.deferred_blanket.map(
      (d) => ['Blanket order beyond near-term window', `${d.order} ${d.qty} on ${d.scheduled_date} (${d.customer})`] as [string, string],
    ),
    ...c.uom_failures.map((u) => ['Skipped, UoM conversion failed', `${u.default_code ?? ''} ${u.error}`] as [string, string]),
    ...c.errors.map((e) => ['Skipped, calculation error', `${e.default_code ?? e.product_id}: ${e.error}`] as [string, string]),
  ]
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} className="border-b border-subtle">
            <td className="py-1 pr-4 text-secondary">{k}</td>
            <td className="py-1 text-primary">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
